/* © 2017 - Copyright of Aetonix Systems Inc - All Rights Reserved. Patent pending.
* Unauthorized copying of this file, via any medium is strictly prohibited
* Proprietary and confidential
* Written by Aetonix, January 01, 2017
* For information or permission request, email info@aetonixsystems.com
*/

import PropTypes from "prop-types";
import React, {
	Component
} from "react";
import {
	View,
	StyleSheet,
} from "react-native";
import memoize from "memoizee";

import StyledIconButton from "ae-base/Base/StyledIconButton";
import StyledButton from "ae-base/Base/StyledButton";
import FormInput from "./FormInput";
import InputLabel from "./Layout/InputLabel";
import errorUtils from "../errorUtils";
import intputStyle from "./Layout/intputStyle";
import ListAdd from "ae-base/List/ListAdd";
import ErrorMessage from "./Layout/ErrorMessage";

var styles = StyleSheet.create({
	container: {
		flexDirection: "row",
		alignItems: "center"
	},
	input: {
		flex: 1
	}
});

export default class InputList extends Component {
	constructor(props) {
		super(props);

		this.update = memoize(updateIndex.bind(this));
		this.removeItem = memoize(removeIndex.bind(this));
		this.add = addItem.bind(this);

		this.state = {
			hasChanged: false,
			list: []
		};
	}
	render() {
		var context = this.context;
		var props = this.props;
		var schema = props.schema;

		var maxLength = schema.maxLength;

		var staticList = schema.static;
		var noAddition = schema.noAddition;

		var onSubmit = props.onSubmit;

		var hasChanged = this.state.hasChanged;
		var defaultValue = (hasChanged ? this.state.list : props.defaultValue) || [];

		var itemSchema = schema.items || {};

		var update = this.update;
		var removeItem = this.removeItem;

		var localize = this.context.localize;
		var add = localize("inputlist.add");
		var remove = localize("inputlist.remove");

		var errorProps = props.errors || [];
		var errors = errorUtils.formatErrors(errorProps);
		var topErrors = errorProps.filter(function (error) {
			return error.field.length === 0;
		});

		var isObject = itemSchema.type === "object";

		var containerStyle = isObject ? intputStyle.subFormContainer : styles.container;

		// Transform consecutive duplicate strings in the array to empty strings to ensure that the same selection is
		// not consecutively repeated.
		const normalizedDefaultValue = defaultValue.map((item, index, arr) =>
			index > 0 && item === arr[index - 1] ? '' : item
		);

		// Used to ensure that the same item on later indices will be filtered out.
		const getFilteredListForItem = (index, value, allValues) => {
			return allValues.filter((item, idx) => idx !== index && item !== value);
		}

		var inputs = normalizedDefaultValue.map(function (data, index) {
			var onItemChange = update(index);
			var onItemRemove = removeItem(index);
			var inputErrors = errors[index];

			const currentFilteredList = getFilteredListForItem(index, data, normalizedDefaultValue);

			var removeButton = null

			if (!staticList) {
				removeButton = isObject ? (
					<View>
						<StyledButton id={props.id + "_remove_" + index} alert icon="trash" title={remove} onPress={onItemRemove} />
					</View>
				) : (
					<View>
						<StyledIconButton id={props.id + "_remove_" + index} color="alert" standard icon="delete" onPress={onItemRemove}  />
					</View>
				);
			}

			var inputStyle = isObject ? null : styles.input;

			if (data === undefined) {
				return null;
			} else {
				return (
					<View style={containerStyle} key={index} >
						<View style={inputStyle}>
							<FormInput
								unsupportedFeatures={props.unsupportedFeatures}
								platform={props.platform}
								id={props.id + "_" + index}
								filterList={currentFilteredList}
								schema={itemSchema}
								defaultValue={data}
								onChange={onItemChange}
								onSubmit={onSubmit}
								errors={inputErrors}
								showCustomAddButton = {props.showCustomAddButton}
							/>
						</View>
						<View style={{alignItems: "center", alignSelf: "center"}}>
							{removeButton}
						</View>
					</View>
				);
			}
		});

		var renderAddButton = null;
		var noMaxLength = !maxLength
		if ((noMaxLength || defaultValue.filter(Boolean).length < maxLength) && !staticList  && !noAddition) {
			renderAddButton = <ListAdd id={context.id_prefix + props.id + "_add"} title={add} onPress={this.add} />
		}
		return (
			<View key={defaultValue.length}>
				<InputLabel schema={schema} />
				<ErrorMessage id={context.id_prefix + props.id + "_error"} errors={topErrors} schema={schema} />
				{inputs}
				{renderAddButton}
			</View>
		);
	}
}

import types from "./Layout/inputTypes";

InputList.propTypes = types;

InputList.contextTypes = {
	id_prefix: PropTypes.string,
	localize: PropTypes.func
};

function updateIndex(index) {
	var component = this;
	return function (value) {
		var defaultValue = component.props.defaultValue;

		var hasChanged = component.state.hasChanged;

		var list;
		if (hasChanged)
			list = component.state.list.slice();
		else
			list = (defaultValue || []).slice();

		list[index] = value;

		component.setState({
			hasChanged: true,
			list: list
		});

		var onChange = component.props.onChange;
		if (onChange)
			onChange(list);
	};
}

function addItem() {
	const component = this;
	const defaultValue = component.props.defaultValue;

	const {state: {hasChanged, list: currentList = []}} = component;

	let list;
	if (hasChanged)
		list = currentList.slice();
	else
		list = (defaultValue || []).slice();

	let newItem;
	if(this.props.schema.items && this.props.schema.items.type === "object")
		newItem = {};
	else newItem = "";

	let newList = list.concat(newItem);

	component.setState({
		hasChanged: true,
		list: newList
	});

	const onChange = component.props.onChange;
	if (onChange)
		onChange(newList);
}

function removeIndex(index) {
	const component = this;
	return function () {
		const defaultValue = component.props.defaultValue;
		const {state: {hasChanged, list: currentList = []}} = component;

		let list;
		if (hasChanged) {
			list = currentList.slice();
		} else {
			list = (defaultValue || []).slice();
		}

		let newList;
		const description = component.props.schema.description;

		// Create a shallow copy of the original list
		const updatedList = list.slice();

		if (description && description.indexOf("ae_careplan") !== -1) {
			// Remove the item by index
			updatedList.splice(index, 1);
		} else {
			// Remove the item by index
			updatedList.splice(index, 1);
			newList = updatedList;
		}

		// Check if the list is empty after the update
		if (updatedList.length === 0) {
			// If empty, maintain undefined
			newList = undefined;
		}

		component.setState({
			hasChanged: true,
			list: newList
		});

		let onChange = component.props.onChange;
		if (onChange) {
			onChange(newList);
		}
	};
}
